import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faStop,
  faCircleNotch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import useVoiceDictation from "../../hooks/useVoiceDictation";
import "./voice.css";

/**
 * Reusable dictation button: idle mic → recording (pulsing halo + live
 * 5-bar level meter + elapsed) → transcribing spinner → onTranscript(text).
 *
 * The level meter reads the mic via getLevel() in its own rAF and mutates
 * transforms directly — zero React re-renders while speaking. Theme-safe
 * (slate/red/blue tokens, dark: variants); halo pulse is reduced-motion
 * gated in voice.css, the meter itself is functional feedback and stays.
 */
const BAR_SPEEDS = [1.9, 2.6, 2.2, 3.1, 2.4];

const MicButton = ({
  onTranscript,
  onError,
  onStatusChange,
  size = "md", // "sm" (compact inputs) | "md"
  title = "Dictate",
  className = "",
  disabled = false,
  /** Increment to programmatically arm recording (e.g. the voice FAB opening
   *  the chat panel pre-armed). 0 = never auto-start. */
  armSignal = 0,
}) => {
  const {
    status,
    elapsed,
    isSupported,
    start,
    toggle,
    cancel,
    getLevel,
    getSilenceProgress,
  } = useVoiceDictation({ onTranscript, onError });

  const barsRef = useRef(null);
  const rafRef = useRef(null);
  const armHandledRef = useRef(0);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // One auto-start per armSignal increment. Consume the signal ONLY when start
  // actually runs — otherwise arming while busy would swallow the increment and
  // the next Alt+M would silently no-op.
  useEffect(() => {
    if (
      armSignal > 0 &&
      armSignal !== armHandledRef.current &&
      status === "idle" &&
      !disabled
    ) {
      armHandledRef.current = armSignal;
      start();
    }
  }, [armSignal, status, disabled, start]);

  // Drive the equalizer bars imperatively while recording, and dim the whole
  // meter as the post-speech silence approaches auto-send — the turn visibly
  // "closes out" instead of cutting off.
  useEffect(() => {
    if (status !== "recording") return undefined;
    const loop = () => {
      const el = barsRef.current;
      if (el) {
        const level = getLevel();
        const t = performance.now() / 1000;
        const bars = el.children;
        for (let i = 0; i < bars.length; i += 1) {
          const wobble = 0.65 + 0.35 * Math.sin(t * BAR_SPEEDS[i] * 3 + i);
          const scale = 0.18 + level * wobble;
          bars[i].style.transform = `scaleY(${Math.min(1, scale)})`;
        }
        const p = getSilenceProgress ? getSilenceProgress() : 0;
        el.style.opacity = String(1 - 0.6 * p);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, getLevel, getSilenceProgress]);

  // Escape discards an active recording OR a pending transcription.
  useEffect(() => {
    if (status !== "recording" && status !== "transcribing") return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        cancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [status, cancel]);

  if (!isSupported) return null;

  const recording = status === "recording";
  const transcribing = status === "transcribing";
  const sm = size === "sm";
  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={transcribing ? cancel : toggle}
      aria-label={
        recording
          ? "Stop recording"
          : transcribing
            ? "Cancel transcription"
            : title
      }
      aria-pressed={recording}
      title={
        recording
          ? "Stop & transcribe (Esc to discard)"
          : transcribing
            ? "Transcribing… (tap or Esc to cancel)"
            : title
      }
      className={`fk-voice-btn group relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ${
        sm ? "h-8 min-w-8 px-1.5" : "h-9 min-w-9 px-2"
      } ${
        recording
          ? "border-red-300 bg-red-50 text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-400"
          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
      } ${className}`}
    >
      {recording && <span className="fk-voice-halo" aria-hidden />}
      {transcribing ? (
        <span className="relative inline-flex items-center justify-center">
          <FontAwesomeIcon
            icon={faCircleNotch}
            className={`animate-spin ${sm ? "text-[12px]" : "text-[13px]"}`}
          />
          <FontAwesomeIcon
            icon={faXmark}
            className="absolute text-[7px] opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          />
        </span>
      ) : recording ? (
        <>
          <FontAwesomeIcon icon={faStop} className={sm ? "text-[10px]" : "text-[11px]"} />
          <span ref={barsRef} className="fk-voice-bars" aria-hidden>
            <span /><span /><span /><span /><span />
          </span>
          <span className="text-[10.5px] font-semibold tabular-nums">{mmss}</span>
        </>
      ) : (
        <FontAwesomeIcon
          icon={faMicrophone}
          className={sm ? "text-[13px]" : "text-[14px]"}
        />
      )}
    </button>
  );
};

export default MicButton;
